import {
  useState,
  useEffect,
  useContext,
  useMemo,
  Dispatch,
  SetStateAction,
} from 'react'
import { fetchData } from '../../../global/utils/fetch'
import { getFuturePercentBookedData } from './api'
import moment from 'moment'
import { COLUMN_DEFINITIONS } from '../scheduling-performance/helpers/column-definitions'
import { ToastNotificationContext } from '../../../global/context/toast-context/ToastNotificationContext'
import { formatDateYyyyMmDd } from '../../../global/utils/date/date-utils-yyyy-mm-dd'
import MultiLineDashboard, {
  MultiLineDashboardColumn,
  AggregateEntry,
} from '../../../global/components/dashboard/MultiLineDashboard'
import { FuturePercentBookedData } from '../../../../../app/types/report_data'

// Data row for each metric type within a location
interface LocationMetricRow {
  d2aValue: string
  d2aTooltip?: string
  metricType: string
  dayValues: (number | string)[]
}

interface FuturePercentBookedProps {
  date: Date
  isTableLoading: boolean
  setIsTableLoading: Dispatch<SetStateAction<boolean>>
}

function FuturePercentBooked({
  date,
  isTableLoading,
  setIsTableLoading,
}: FuturePercentBookedProps) {
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)
  const [tableState, setTableState] = useState<FuturePercentBookedData | null>(
    null,
  )
  const today = moment(date).utc().startOf('day')

  async function fetchDashboardData() {
    try {
      setIsTableLoading(true)
      const data = await fetchData<FuturePercentBookedData>(
        getFuturePercentBookedData(date),
      )
      setTableState(data)
    } catch (err) {
      console.error(err)
      setTableState(null)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: `There is no data for ${formatDateYyyyMmDd(date)}`,
        severity: 'error',
      })
    } finally {
      setIsTableLoading(false)
    }
  }

  useEffect(() => {
    fetchDashboardData()
  }, [date])

  // Generate column headers for the 14 days + 3 summary columns
  const dayHeaders = useMemo(() => {
    const headers: string[] = []
    for (let i = 1; i <= 14; i++) {
      headers.push(`(${i}) ${today.clone().add(i, 'day').format('M/D')}`)
    }
    headers.push('7day%', '10day%', '14day%')
    return headers
  }, [today])

  // Build columns for MultiLineDashboard
  const columns: MultiLineDashboardColumn<LocationMetricRow>[] = useMemo(() => {
    const cols: MultiLineDashboardColumn<LocationMetricRow>[] = [
      {
        Header: 'D2A',
        accessor: 'd2aValue',
        Cell: ({ row }) => ({
          value: row.d2aValue,
          tooltip: row.d2aTooltip,
        }),
      },
      {
        Header: '',
        accessor: 'metricType',
      },
    ]

    // Add day columns
    dayHeaders.forEach((header, idx) => {
      cols.push({
        Header: header,
        accessor: (row) => row.dayValues[idx],
      })
    })

    return cols
  }, [dayHeaders])

  // Transform data into aggregates for MultiLineDashboard
  const aggregates: AggregateEntry<LocationMetricRow>[] = useMemo(() => {
    if (!tableState) return []

    const locationData = tableState.futurePercentBookedLocation
    const locationKeys = Object.keys(locationData).sort()

    return locationKeys.map((locationName) => {
      const data = locationData[locationName]

      const rows: LocationMetricRow[] = [
        {
          d2aValue: `Min: ${data.d2a.minimum}`,
          d2aTooltip: COLUMN_DEFINITIONS.FUTURE_PERCENT_BOOKED.d2a.minimum,
          metricType: 'Work Orders',
          dayValues: data.fourteenDayWorkOrderCounts,
        },
        {
          d2aValue: `Avg: ${data.d2a.thirtyDayAverage}`,
          d2aTooltip: COLUMN_DEFINITIONS.FUTURE_PERCENT_BOOKED.d2a.average,
          metricType: 'Available Slots',
          dayValues: data.fourteenDayJobCapacities,
        },
        {
          d2aValue: `Max: ${data.d2a.maximum}`,
          d2aTooltip: COLUMN_DEFINITIONS.FUTURE_PERCENT_BOOKED.d2a.maximum,
          metricType: '%Booked',
          dayValues: data.percentBookedArray,
        },
      ]

      return {
        key: locationName,
        name: locationName,
        data: rows,
      }
    })
  }, [tableState])

  // Build grand totals
  const grandTotals = useMemo(() => {
    if (!tableState) return undefined

    const [totalD2A, ...totalPercentBooked] = tableState.totalData
    // First value is D2A, second is empty (metric type column), rest are the percent values
    return [totalD2A, '%Booked', ...totalPercentBooked]
  }, [tableState])

  return (
    <MultiLineDashboard<LocationMetricRow>
      columns={columns}
      aggregates={aggregates}
      aggregateColumnHeader='Location'
      isLoading={isTableLoading}
      totals={grandTotals}
      totalsLabel='TRMI Total'
      getRowKey={(row, aggregateKey, idx) =>
        `${aggregateKey}-${row.metricType}-${idx}`
      }
      noDataMessage='No data available.'
    />
  )
}

export default FuturePercentBooked
